/**
 * Global React error boundary.
 * Catches runtime errors anywhere in the tree, shows a branded fallback card,
 * and quietly POSTs the error to /api/debug/frontend-error for backend logs.
 */
import React from 'react';
import axios from 'axios';

const API_URL = window.location.origin;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true, errorId: Math.random().toString(36).slice(2, 10) };
  }

  componentDidCatch(error, info) {
    // Always log to browser console so devs see the full stack
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);

    // Silent report to backend — never throws or blocks the fallback UI
    try {
      const payload = {
        error_id: this.state.errorId,
        message: String(error?.message || error),
        stack: String(error?.stack || ''),
        component_stack: String(info?.componentStack || ''),
        url: window.location.href,
        user_agent: navigator.userAgent,
      };
      axios.post(`${API_URL}/api/debug/frontend-error`, payload, { timeout: 5000 })
        .catch(() => { /* swallow — never surface reporting errors to the user */ });
    } catch (_) {
      /* noop */
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 bg-cream"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-sand p-8 text-center">
          <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-terracotta/10 flex items-center justify-center">
            <span className="text-terracotta text-2xl font-heading">!</span>
          </div>
          <h1 className="text-2xl font-heading text-forest mb-2">
            Something went sideways, Sis.
          </h1>
          <p className="text-gray-600 text-sm mb-1">
            We hit an unexpected hiccup loading this page. Your data is safe.
          </p>
          <p className="text-gray-400 text-xs mb-6">
            Reference: {this.state.errorId}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload-btn"
              className="px-5 py-2.5 rounded-full bg-forest text-white text-sm font-medium hover:bg-forest/90 transition-colors"
            >
              Refresh the page
            </button>
            <button
              onClick={this.handleHome}
              data-testid="error-boundary-home-btn"
              className="px-5 py-2.5 rounded-full border border-forest text-forest text-sm font-medium hover:bg-forest/5 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
